import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Pets as PetsIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CreditCard as CreditCardIcon,
  Description as DocumentIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const PatientFormDialog = React.memo(function PatientFormDialog({
  open,
  onClose,
  onSave,
  editingPatient,
  initialData,
  loading,
  convertFileToBase64,
  onSnackbar
}) {
  const [patientData, setPatientData] = useState(initialData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialData?.foto || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPatientData(initialData);
    setSelectedFile(null);
    setPreviewUrl(initialData?.foto || null);
    setUploadingPhoto(false);
  }, [open, initialData]);

  const handleFieldChange = useCallback((field, value) => {
    setPatientData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      onSnackbar('error', 'Por favor selecciona una imagen valida (JPG, PNG, WEBP)');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      onSnackbar('error', 'La imagen es muy grande. El tamano maximo es 10MB');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    onSnackbar('success', 'Foto seleccionada correctamente');
  }, [onSnackbar]);

  const handleRemovePhoto = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setPatientData((prev) => ({ ...prev, foto: '' }));

    const fileInput = document.getElementById('patient-form-photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }

    onSnackbar('info', 'Foto removida');
  }, [onSnackbar]);

  const handleSave = useCallback(async () => {
    if (!patientData?.nombre || !patientData?.propietario) {
      onSnackbar('error', 'Por favor completa los campos obligatorios (Nombre del paciente y Propietario)');
      return;
    }

    try {
      let fotoBase64 = patientData.foto;

      if (selectedFile) {
        setUploadingPhoto(true);
        fotoBase64 = await convertFileToBase64(selectedFile);
      }

      await onSave({
        ...patientData,
        foto: fotoBase64
      });
    } catch (error) {
      onSnackbar('error', `Error guardando paciente: ${error.message}`);
    } finally {
      setUploadingPhoto(false);
    }
  }, [patientData, selectedFile, convertFileToBase64, onSave, onSnackbar]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: { xs: 2, sm: 3 },
          maxHeight: { xs: '100vh', sm: '95vh' },
          overflow: 'hidden',
          m: { xs: 0.5, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: '90%' }
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#c46494',
          color: 'white',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PetsIcon sx={{ mr: 2, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {editingPatient ? 'Editar Paciente' : 'Nuevo Paciente'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {editingPatient ? 'Actualiza la informacion del paciente' : 'Registra un nuevo paciente en el sistema'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Cerrar">
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
        <Box sx={{ p: 4 }}>
          <Card sx={{ mb: 4, overflow: 'visible' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ color: '#c46494', mb: 3, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                <PetsIcon sx={{ mr: 1 }} />
                Informacion del Paciente
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Nombre del Paciente" required value={patientData.nombre} onChange={(e) => handleFieldChange('nombre', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Especie</InputLabel>
                    <Select value={patientData.especie} onChange={(e) => handleFieldChange('especie', e.target.value)} label="Especie">
                      <MenuItem value="Canino">Canino</MenuItem>
                      <MenuItem value="Felino">Felino</MenuItem>
                      <MenuItem value="Ave">Ave</MenuItem>
                      <MenuItem value="Roedor">Roedor</MenuItem>
                      <MenuItem value="Otro">Otro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Raza" value={patientData.raza} onChange={(e) => handleFieldChange('raza', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sexo</InputLabel>
                    <Select value={patientData.sexo} onChange={(e) => handleFieldChange('sexo', e.target.value)} label="Sexo">
                      <MenuItem value="Macho">Macho</MenuItem>
                      <MenuItem value="Hembra">Hembra</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Fecha de Nacimiento" type="date" value={patientData.fechaNacimiento} onChange={(e) => handleFieldChange('fechaNacimiento', e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Peso (kg)" type="number" value={patientData.peso} onChange={(e) => handleFieldChange('peso', e.target.value)} inputProps={{ step: '0.1', min: '0' }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Color" value={patientData.color} onChange={(e) => handleFieldChange('color', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Numero de Microchip (ID)" value={patientData.microchip} onChange={(e) => handleFieldChange('microchip', e.target.value.trim() || 'no')} placeholder="Escriba 'no' si no tiene microchip" />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 2, p: 3, border: '2px dashed #c46494', borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" sx={{ color: '#c46494', mb: 2, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                      <PhotoCameraIcon sx={{ mr: 1 }} />
                      Foto del Paciente
                    </Typography>

                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Avatar sx={{ width: 120, height: 120, bgcolor: '#c46494' }} src={previewUrl}>
                            {!previewUrl && 'Foto'}
                          </Avatar>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input id="patient-form-photo-upload" type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" component="label" htmlFor="patient-form-photo-upload" startIcon={uploadingPhoto ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />} disabled={uploadingPhoto || loading} sx={{ bgcolor: '#c46494', '&:hover': { bgcolor: '#a05080' } }}>
                              {uploadingPhoto ? 'Procesando...' : 'Seleccionar Foto'}
                            </Button>

                            {previewUrl && (
                              <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleRemovePhoto}>
                                Remover
                              </Button>
                            )}
                          </Box>

                          {selectedFile && (
                            <Alert severity="success">
                              Archivo seleccionado: {selectedFile.name} - {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </Alert>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 4, overflow: 'visible' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ color: '#c46494', mb: 3, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                <PersonIcon sx={{ mr: 1 }} />
                Informacion del Propietario
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Nombre del Propietario" required value={patientData.propietario} onChange={(e) => handleFieldChange('propietario', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Apellido" value={patientData.propietarioApellido} onChange={(e) => handleFieldChange('propietarioApellido', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Telefono" value={patientData.propietarioTelefono} onChange={(e) => handleFieldChange('propietarioTelefono', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Email" type="email" value={patientData.propietarioEmail} onChange={(e) => handleFieldChange('propietarioEmail', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Cedula/Identificacion" value={patientData.propietarioCedula} onChange={(e) => handleFieldChange('propietarioCedula', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Ciudad" value={patientData.propietarioCiudad} onChange={(e) => handleFieldChange('propietarioCiudad', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Direccion Completa" value={patientData.propietarioDireccion} onChange={(e) => handleFieldChange('propietarioDireccion', e.target.value)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 4, overflow: 'visible' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ color: '#c46494', mb: 3, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                <PhoneIcon sx={{ mr: 1 }} />
                Contacto de Emergencia
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Nombre Completo" value={patientData.contactoEmergenciaNombre} onChange={(e) => handleFieldChange('contactoEmergenciaNombre', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Telefono de Emergencia" value={patientData.contactoEmergenciaTelefono} onChange={(e) => handleFieldChange('contactoEmergenciaTelefono', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Relacion" value={patientData.contactoEmergenciaRelacion} onChange={(e) => handleFieldChange('contactoEmergenciaRelacion', e.target.value)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ overflow: 'visible' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ color: '#c46494', mb: 3, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                <DocumentIcon sx={{ mr: 1 }} />
                Observaciones Adicionales
              </Typography>
              <TextField fullWidth label="Observaciones sobre el paciente" multiline rows={4} value={patientData.observaciones} onChange={(e) => handleFieldChange('observaciones', e.target.value)} />
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 4, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0', gap: 2, justifyContent: 'center' }}>
        <Button onClick={onClose} variant="outlined" size="large" disabled={loading || uploadingPhoto} startIcon={<ClearIcon />}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          disabled={loading || uploadingPhoto}
          startIcon={(loading || uploadingPhoto) ? <CircularProgress size={20} color="inherit" /> : <PetsIcon />}
          sx={{ bgcolor: '#c46494', '&:hover': { bgcolor: '#a05080' } }}
        >
          {(loading || uploadingPhoto) ? 'Guardando...' : `${editingPatient ? 'Actualizar' : 'Guardar'} Paciente`}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default PatientFormDialog;
